import { useEffect, useState } from 'react';

import { Modal } from './Modal';
import { SparkIcon, MonitorIcon } from './Icons';
import {
  clearDeferredInstallPrompt,
  getDeferredInstallPrompt,
  isIosBrowser,
  isStandaloneDisplayMode,
  subscribeInstallPrompt,
  type BeforeInstallPromptEvent,
} from '@/lib/pwa-install';

const SESSION_DISMISS_KEY = '@slotlooks/pwa-install-session-dismissed';

function readSessionDismissed() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.sessionStorage.getItem(SESSION_DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeSessionDismissed() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(SESSION_DISMISS_KEY, 'true');
  } catch {
    // Ignore persistence failures.
  }
}

export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(() =>
    getDeferredInstallPrompt()
  );
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneDisplayMode());
  const [sessionDismissed, setSessionDismissed] = useState(() => readSessionDismissed());
  const [isOpen, setIsOpen] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  const canNativeInstall = Boolean(deferredPrompt) && !isStandalone && !sessionDismissed;
  const canIosHint = isIosBrowser() && !isStandalone && !sessionDismissed && !deferredPrompt;
  const shouldOffer = canNativeInstall || canIosHint;

  useEffect(() => subscribeInstallPrompt(setDeferredPrompt), []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia?.('(display-mode: standalone)');
    if (!mediaQuery) {
      return;
    }

    const handleChange = () => {
      setIsStandalone(isStandaloneDisplayMode());
    };

    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // First authenticated open in this browser session → ask to install.
  useEffect(() => {
    if (!shouldOffer || isOpen) {
      return;
    }

    const timer = window.setTimeout(() => {
      setIsOpen(true);
      setIosHint(canIosHint);
    }, 700);

    return () => window.clearTimeout(timer);
  }, [canIosHint, isOpen, shouldOffer]);

  function dismissForSession() {
    writeSessionDismissed();
    setSessionDismissed(true);
    setIsOpen(false);
  }

  async function handleInstall() {
    if (!deferredPrompt) {
      return;
    }

    const promptEvent = deferredPrompt;
    setIsOpen(false);
    clearDeferredInstallPrompt();
    setDeferredPrompt(null);

    try {
      await promptEvent.prompt();
      const result = await promptEvent.userChoice;

      if (result.outcome === 'accepted') {
        setIsStandalone(true);
        writeSessionDismissed();
        setSessionDismissed(true);
      } else {
        dismissForSession();
      }
    } catch {
      dismissForSession();
    }
  }

  if (!shouldOffer && !isOpen) {
    return null;
  }

  if (isOpen) {
    return (
      <Modal
        open={isOpen}
        title={iosHint ? 'Add SlotLooks to Home Screen' : 'Install SlotLooks'}
        description={
          iosHint
            ? 'Install from your browser menu so SlotLooks opens like an app.'
            : 'Install once for faster access and a full-screen app feel.'
        }
        onClose={dismissForSession}>
        <div className="field-grid">
          <div className="notice install-note">
            <strong className="install-note-title">
              <SparkIcon size={16} />
              {iosHint ? 'Install steps' : 'Ready to install'}
            </strong>
            {iosHint ? (
              <span>
                Tap Share, then <strong>Add to Home Screen</strong> (Safari) or use the browser menu on iOS. After that,
                open SlotLooks from your home screen.
              </span>
            ) : (
              <span>Add SlotLooks to your home screen or desktop for quicker launches.</span>
            )}
            <span className="badge soft fit-content">
              <MonitorIcon size={14} />
              Works offline for the app shell
            </span>
          </div>

          <div className="button-row">
            {iosHint ? (
              <button className="primary-button" type="button" onClick={dismissForSession}>
                Got it
              </button>
            ) : (
              <button className="primary-button" type="button" onClick={() => void handleInstall()}>
                Install app
              </button>
            )}
            <button className="ghost-button" type="button" onClick={dismissForSession}>
              Not now
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  if (!shouldOffer) {
    return null;
  }

  return (
    <div className="pwa-install-banner" role="region" aria-label="Install SlotLooks">
      <div className="pwa-install-copy">
        <SparkIcon size={16} />
        <span>{iosHint || canIosHint ? 'Add SlotLooks to your Home Screen.' : 'Install SlotLooks for a faster app experience.'}</span>
      </div>
      <div className="pwa-install-actions">
        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setIosHint(canIosHint);
            setIsOpen(true);
          }}>
          {canIosHint ? 'How' : 'Install'}
        </button>
        <button className="ghost-button" type="button" onClick={dismissForSession}>
          Not now
        </button>
      </div>
    </div>
  );
}
